import { Computer, Delete, Trash, User } from 'lucide-react';
import CopyButton from '../../../components/CopyButton';
import EmptyState from '../../../components/EmptyState';
import type { OcdInfo } from '../services/ocdProfileTypes';
import { formatTimeAgo } from '../../../utils/timeUtils';
import { ButtonWithConfirmation } from '../../../components/ButtonWithConfirmation';

interface OcdInfoSectionProps {
    title: string;
    items: string[];
    emptyMessage: string;
    icon: React.ReactNode;
}

interface DisplayOcdInfoProps {
    ocdInfo: OcdInfo;
    onDelete: () => Promise<void>;
}

const OcdInfoSection = ({ items, icon }: OcdInfoSectionProps) => {
    const hasItems = items && items.length > 0;
    const itemsText = hasItems ? items.join(', ') : '';

    return (
        <div className="border-b border-gray-200 last:border-b-0 pb-2 last:pb-0">
            <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                    <div className="text-blue-600">
                        {icon}
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {items.map((item, index) => (
                            <span
                                key={index}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-900 border border-blue-200"
                            >
                                {item}
                            </span>
                        ))}
                    </div>
                </div>
                {hasItems && (
                    <div className="space-y-1 ml-2">
                        <p className="text-xs text-gray-400">
                            {items.length} item{items.length !== 1 ? 's' : ''}
                        </p>
                        <CopyButton text={itemsText} />
                    </div>
                )}
            </div>
        </div >
    );
};

export const DisplayOcdInfo = ({ ocdInfo, onDelete }: DisplayOcdInfoProps) => {
    // Check if both arrays are empty
    const hasNoData = (!ocdInfo.userProvided || ocdInfo.userProvided.length === 0) &&
        (!ocdInfo.inferred || ocdInfo.inferred.length === 0);

    if (hasNoData) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900">{ocdInfo.id}</h3>
                <EmptyState
                    title="No OCD Information Available"
                    description="No OCD-related information has been provided by the user or inferred by the system yet."
                    icon={
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    }
                />
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-3 py-2 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900 break-all">{ocdInfo.id}</h3>
                <div className="flex flex-wrap items-center gap-1">
                    <span className="text-xs text-gray-500" title={ocdInfo.createdAt?.toLocaleString()}>Created: {formatTimeAgo(ocdInfo.createdAt)}</span>
                    <span className="text-xs text-gray-500" title={ocdInfo.updatedAt?.toLocaleString()}> Updated: {formatTimeAgo(ocdInfo.updatedAt)}</span>
                    <ButtonWithConfirmation onClick={onDelete} firstText="Delete" secondText="Really delete?" firstIcon={<Trash className="w-4 h-4" />} secondIcon={<Delete className="w-4 h-4" />} firstVariant="outline" secondVariant="danger" />
                </div>
            </div>

            <div className="p-3 space-y-2">
                <OcdInfoSection
                    title="User Provided"
                    items={ocdInfo.userProvided || []}
                    emptyMessage="No user-provided OCD information available."
                    icon={<User className="w-4 h-4" />}
                />

                <OcdInfoSection
                    title="System Inferred"
                    items={ocdInfo.inferred || []}
                    emptyMessage="No system-inferred OCD information available."
                    icon={<Computer className="w-4 h-4" />}
                />
            </div>
        </div>
    );
};